/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
} from '@ant-design/icons'
import { Tooltip } from 'antd'
import { DIVISION_FULL_MESSAGE } from '@sportninja/common/features/registration/startStep/divisionRole'
import AgeIcon from 'src/components/ts-components/icons/AgeIcon'
import CoedGenderIcon from 'src/components/icons/CoedGenderIcon'
import MaleGenderIcon from 'src/components/icons/MaleGenderIcon'
import FemaleGenderIcon from 'src/components/icons/FemaleGenderIcon'
import colors from '@sportninja/common/constants/appColors'
// import { formatCurrency } from '@sportninja/common/utils/utils'
import Text from 'src/components/ts-components/Text'
import React, { useCallback, useMemo, useState } from 'react'
import { formatCurrency } from '@sportninja/common/utils/utils'
import { Division } from '@sportninja/common/types'
import AdditionalInfoModal from 'src/components/RegistrationEventCard/AdditionalInfoModal'

/**
 * Props interface for the DivisionCard component
 */
interface DivisionCardProps {
  division: Division
  isSelected: boolean
  currencyName?: string
  onSelect: (division: Division) => void
  isTeamRegistrationEnabled: boolean
  isPlayerRegistrationEnabled: boolean
  hasSelectedDivision: boolean
}

/**
 * Styles for the DivisionCard component
 */
const styles = {
  card: (
    isSelected: boolean,
    fullDivision: boolean,
    hasSelectedDivision: boolean
  ) => css`
    width: 100%;
    display: flex;
    padding: 16px;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
    border-radius: 16px;
    border: ${isSelected
      ? `2px solid ${colors.PRIMARY}`
      : `1px solid ${colors.NEUTRAL_500}`};
    cursor: ${fullDivision ? 'not-allowed' : 'pointer'};
    opacity: ${fullDivision
      ? 0.7
      : hasSelectedDivision && !isSelected
      ? 0.5
      : 1};
    background-color: ${isSelected ? colors.NEUTRAL_700 : 'transparent'};
    min-height: 150px;
  `,

  /* One amount inside a half. Equal columns so the fee and its deposit line up
     as a pair rather than the deposit trailing off whatever width the fee took. */
  feeColumn: css`
    flex: 1;
    min-width: 0;
  `,

  /* Divider between a fee and its deposit, matching the one that separates the
     team half from the player half. */
  feeDivider: css`
    width: 1px;
    height: 40px;
    background-color: ${colors.NEUTRAL_500};
    flex-shrink: 0;
  `,

  infoContainer: css`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    align-self: stretch;
    width: 100%;
    justify-content: space-between;
    align-items: flex-start;
    /* min-height: 80px; */
  `,

  detailsColumn: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 4px;
    align-self: flex-start;
  `,

  costColumn: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-end;
    gap: 4px;
  `,

  statusContainer: css`
    display: flex;
    justify-content: space-between;
    width: 100%;
    gap: 8px;
    flex: 1;
    flex-wrap: wrap;
    align-self: flex-end;
  `,

  // Ensures the section below the description (gender/age/day + fees)
  // is anchored to the bottom of the card regardless of description length
  bottomSection: css`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: auto;
  `,

  detailsRow: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 4px;
  `,

  divider: css`
    border-bottom: 1px solid ${colors.NEUTRAL_500};
    width: 100%;
    margin: 8px 0;
  `,

  statusBadge: (backgroundColor: string, borderColor: string) => css`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 4px;
    padding: 8px 8px;
    border-radius: 8px;
    background-color: ${backgroundColor};
    height: 30px;
    /* The badges stack in a column, so this is what keeps each one only as wide
       as its own label instead of stretching to match the widest sibling. */
    align-self: flex-end;
    /* A column flex child would otherwise be squashed by the fixed height
       above once two badges share the space. */
    flex-shrink: 0;
    border: 1px solid ${borderColor};
  `,
}

/**
 * DivisionCard component displays information about a division including
 * its details, costs, and availability status
 */
export const DivisionCard: React.FC<DivisionCardProps> = ({
  division,
  isSelected,
  currencyName,
  onSelect,
  isTeamRegistrationEnabled,
  isPlayerRegistrationEnabled,
  hasSelectedDivision,
}) => {
  const [isDescModalOpen, setIsDescModalOpen] = useState(false)
  const showExistingRegistrationsTeams =
    division.show_existing_registrations_teams
  // Calculate division statistics
  // `max_teams` is null for an uncapped division, which is the common case in
  // real payloads. Without this guard `spotsLeft` goes negative and the
  // running-out warning fires on a division that can never run out.
  const totalSpots = division.max_teams
  const teamRegistered = division.team_count_enrolled
  const isCapped = totalSpots !== null && totalSpots !== undefined
  const spotsLeft = isCapped ? totalSpots - teamRegistered : Infinity
  const areLessThanOneThirdSpotsLeft = isCapped && spotsLeft < totalSpots / 3
  const fullDivision =
    division?.max_teams === null || division?.max_teams === undefined
      ? false
      : spotsLeft <= 0 && !isPlayerRegistrationEnabled

  const bothRegistrationDisabled =
    !isTeamRegistrationEnabled && !isPlayerRegistrationEnabled

  /*
   * Whether each half has an amount worth printing.
   *
   * A division that charges nothing used to render "Team Fee / N/A" — a label
   * and a non-answer taking a whole column to say the division is free. The
   * half is dropped instead, and with it the taxes note when neither half is
   * left, since there is no amount for tax to be added to.
   */
  const hasTeamAmounts =
    isTeamRegistrationEnabled &&
    (Number(division.team_cost) > 0 ||
      Boolean(division.deposit_required && Number(division.deposit) > 0))

  const hasPlayerAmounts =
    isPlayerRegistrationEnabled &&
    (Number(division.player_cost) > 0 ||
      Boolean(
        division.player_deposit_required && Number(division.player_deposit) > 0
      ))

  const description = division?.description || ''
  const trimmedDescription = description.trim()
  const isLongDescription = trimmedDescription.length > 256

  const descriptionHtml = useMemo(() => {
    const escapeHtml = (str: string) =>
      str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    return `<p>${escapeHtml(trimmedDescription)}</p>`
  }, [trimmedDescription])

  const handleOpenDescModal = useCallback(() => {
    if (isLongDescription) {
      setIsDescModalOpen(true)
    }
  }, [isLongDescription])

  const handleCloseDescModal = useCallback(() => {
    setIsDescModalOpen(false)
  }, [])

  /**
   * Handle card selection
   */
  const handleClick = () => {
    if (fullDivision || bothRegistrationDisabled) {
      return
    }
    onSelect(division)
  }

  /**
   * Render the appropriate gender icon based on division gender
   */
  const renderGenderIcon = () => {
    const genderName = division?.division_gender?.name || ''
    const normalized = genderName.trim().toLowerCase()

    if (normalized === 'male') {
      return <MaleGenderIcon size={16} color={colors.PRIMARY} />
    }
    if (normalized === 'female' || normalized === 'women') {
      return <FemaleGenderIcon size={16} color={colors.PRIMARY} />
    }
    // Default to Coed icon
    return <CoedGenderIcon size={16} color={colors.PRIMARY} />
  }

  /**
   * Render the status badges for the division
   */
  const renderStatusBadges = () => {
    if (bothRegistrationDisabled) {
      return (
        <div
          css={styles.statusBadge(
            'rgba(121, 123, 126, 0.20)',
            colors.NEUTRAL_500
          )}
        >
          <CheckCircleOutlined style={{ color: colors.WHITE }} />
          <Text variant='paragraphXSmall' color={colors.WHITE} weight='regular'>
            Registration Closed
          </Text>
        </div>
      )
    }

    // A full division carries NO badge: "Division Full" and "0 Spots Left" were
    // two tags for one fact, and the card already says it — the fill dims and
    // the cursor refuses. The reason is given once, in the tooltip on the card
    // itself, matching how a full team card explains the same state.
    if (fullDivision) {
      return null
    }

    return (
      <>
        {division.team_count_enrolled > 0 &&
          showExistingRegistrationsTeams &&
          isTeamRegistrationEnabled && (
            <div
              css={styles.statusBadge(
                'rgba(57, 187, 85, 0.20)',
                colors.SUCCESS_200
              )}
            >
              <CheckCircleOutlined style={{ color: colors.WHITE }} />
              <Text
                variant='paragraphXSmall'
                color={colors.WHITE}
                weight='regular'
              >
                {division.team_count_enrolled} Teams{' '}
                {areLessThanOneThirdSpotsLeft && spotsLeft > 0
                  ? 'Regd.'
                  : 'Registered'}
              </Text>
            </div>
          )}
        {areLessThanOneThirdSpotsLeft &&
          spotsLeft > 0 &&
          showExistingRegistrationsTeams && (
            <div
              css={styles.statusBadge(
                'rgba(223, 66, 66, 0.20)',
                colors.ERROR_200
              )}
            >
              <ClockCircleOutlined style={{ color: colors.WHITE }} />
              <Text
                variant='paragraphXSmall'
                color={colors.WHITE}
                weight='regular'
              >
                {spotsLeft} Spots Left
              </Text>
            </div>
          )}
      </>
    )
  }

  const card = (
    <div
      onClick={handleClick}
      css={styles.card(
        isSelected,
        fullDivision || bothRegistrationDisabled,
        hasSelectedDivision
      )}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          width: 100%;
          flex-wrap: nowrap;
          gap: 8px;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 1 1 auto;
            min-width: 0; /* allow children to shrink */
          `}
        >
          {isSelected && (
            <CheckCircleOutlined
              css={css`
                color: ${colors.PRIMARY};
                font-size: 20px;
              `}
            />
          )}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 2px;
              flex: 1 1 auto;
              min-width: 0; /* prevent pushing the badge */
            `}
          >
            <Text
              variant='heading5'
              weight='semiBold'
              color={isSelected ? colors.PRIMARY : colors.WHITE}
            >
              {division.name}
            </Text>
          </div>
        </div>
        {/* Stacked, not side by side: two badges in a row took most of the
            header and pushed the division name onto a second line. In a column
            they occupy one badge's width, and the title keeps the rest.
            `align-items: flex-end` keeps them right-aligned to the card edge
            while each stays only as wide as its own label. */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 8px;
            flex-shrink: 0;
            align-self: flex-start;
          `}
        >
          {renderStatusBadges()}
        </div>
      </div>

      {trimmedDescription.length > 0 && (
        <div
          css={css`
            width: 100%;
            margin-top: 4px;
          `}
        >
          <Text
            variant='paragraphXSmall'
            color={colors.NEUTRAL_50}
            weight='regular'
            style={
              'display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis; white-space: normal; word-break: break-word;'
            }
          >
            {trimmedDescription}
          </Text>
          {isLongDescription && (
            <div
              css={css`
                margin-top: 4px;
                cursor: pointer;
                text-align: right;
                &:hover {
                  opacity: 0.8;
                }
              `}
              onClick={handleOpenDescModal}
            >
              <Text
                variant='paragraphXSmall'
                color={colors.PRIMARY}
                weight='regular'
              >
                Read More
              </Text>
            </div>
          )}
        </div>
      )}

      <div css={styles.bottomSection}>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
            margin-top: 8px;
          `}
        >
          <div
            css={[
              styles.detailsRow,
              css`
                display: ${!division?.division_gender?.name ||
                division?.division_gender?.name === 'N/A'
                  ? 'none'
                  : 'flex'};
              `,
            ]}
          >
            {renderGenderIcon()}
            <Text
              variant='paragraphXSmall'
              color={colors.WHITE}
              weight='medium'
            >
              {division?.division_gender?.name}
            </Text>
          </div>
          {/* if not division_age_v2, display none */}
          <div
            css={[
              styles.detailsRow,
              css`
                display: ${division?.division_age_v2 &&
                division?.division_age_v2?.trim().length > 0
                  ? 'flex'
                  : 'none'};
              `,
            ]}
          >
            <AgeIcon size={16} color={colors.PRIMARY} />
            <Text
              variant='paragraphXSmall'
              color={colors.WHITE}
              weight='medium'
              style={'margin-top: 2px;'}
            >
              {division?.division_age_v2}
            </Text>
          </div>
          <div
            css={[
              styles.detailsRow,
              css`
                display: ${division?.division_day_of_week_v2 &&
                division?.division_day_of_week_v2?.trim().length > 0
                  ? 'flex'
                  : 'none'};
              `,
            ]}
          >
            <CalendarOutlined
              css={css`
                color: ${colors.PRIMARY};
                font-size: 16px;
              `}
            />
            <Text
              variant='paragraphXSmall'
              color={colors.WHITE}
              weight='medium'
            >
              {division?.division_day_of_week_v2}
            </Text>
          </div>
        </div>

        {/* Separates the attribute row from the fees. Gated with them: with no
            amounts to show, this was a rule drawn above empty space. */}
        {(hasTeamAmounts || hasPlayerAmounts) && <div css={styles.divider} />}

        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            gap: 8px;
          `}
        >
          {/* Left side - Team costs */}
          {hasTeamAmounts && (
            <div
              css={css`
                display: flex;
                gap: 16px;
                align-items: center;
                flex: 1;
                ${hasPlayerAmounts && 'max-width: 50%'};
              `}
            >
              <div css={styles.feeColumn}>
                <Text
                  variant='paragraphXSmall'
                  color={colors.NEUTRAL_50}
                  weight='regular'
                >
                  Team Fee
                </Text>
                <Text
                  variant='paragraphSmall'
                  // color={isSelected ? colors.PRIMARY : colors.NEUTRAL_0}
                  color={colors.PRIMARY}
                  weight='bold'
                >
                  {division.team_cost > 0
                    ? formatCurrency(division.team_cost, currencyName)
                    : 'N/A'}
                </Text>
              </div>
              {division.deposit_required && division.deposit > 0 && (
                <>
                  <div css={styles.feeDivider} />
                  <div css={styles.feeColumn}>
                    <Text
                      variant='paragraphXSmall'
                      color={colors.NEUTRAL_50}
                      weight='regular'
                    >
                      Team Deposit
                    </Text>
                    <Text
                      variant='paragraphSmall'
                      color={colors.NEUTRAL_0}
                      weight='bold'
                    >
                      {formatCurrency(division.deposit, currencyName)}
                    </Text>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Vertical Separator — only when there are two halves to separate. */}
          {hasTeamAmounts && hasPlayerAmounts && (
            <div
              css={css`
                width: 1px;
                height: 40px;
                background-color: ${colors.NEUTRAL_500};
                /* margin: 0 8px; */
                flex-shrink: 0;
              `}
            />
          )}

          {/* Player costs */}
          {hasPlayerAmounts && (
            <div
              css={css`
                display: flex;
                gap: 16px;
                align-items: center;
                flex: 1;
                ${hasTeamAmounts && 'max-width: 50%'};
                ${hasTeamAmounts && 'margin-left: 8px'};
              `}
            >
              <div css={styles.feeColumn}>
                <Text
                  variant='paragraphXSmall'
                  color={colors.NEUTRAL_50}
                  weight='regular'
                >
                  Player Fee
                </Text>
                <Text
                  variant='paragraphSmall'
                  // color={isSelected ? colors.PRIMARY : colors.NEUTRAL_0}
                  color={colors.PRIMARY}
                  weight='bold'
                >
                  {Number(division.player_cost) > 0
                    ? formatCurrency(Number(division.player_cost), currencyName)
                    : 'N/A'}
                </Text>
              </div>
              {division.player_deposit_required &&
                division.player_deposit != null &&
                division.player_deposit > 0 && (
                  <>
                    <div css={styles.feeDivider} />
                    <div css={styles.feeColumn}>
                      <Text
                        variant='paragraphXSmall'
                        color={colors.NEUTRAL_50}
                        weight='regular'
                      >
                        Player Deposit
                      </Text>
                      <Text
                        variant='paragraphSmall'
                        color={colors.NEUTRAL_0}
                        weight='bold'
                      >
                        {formatCurrency(division.player_deposit, currencyName)}
                      </Text>
                    </div>
                  </>
                )}
            </div>
          )}
        </div>

        {/* The amounts above are base costs. Tax and the League/Payment
            Processing Fees are calculated at checkout, so the card would
            otherwise understate what the registrant actually pays. Stated once
            beneath the row rather than per-label — four amounts share it, and
            the row has no horizontal budget to spare. */}
        {(hasTeamAmounts || hasPlayerAmounts) && (
          <div
            css={css`
              margin-top: 12px;
            `}
          >
            <Text
              variant='paragraphXSmall'
              color={colors.NEUTRAL_50}
              weight='regular'
            >
              * Additional taxes and fees apply at checkout.
            </Text>
          </div>
        )}
      </div>
      {isLongDescription && (
        <AdditionalInfoModal
          isOpen={isDescModalOpen}
          onClose={handleCloseDescModal}
          content={descriptionHtml}
          title='Description'
        />
      )}
    </div>
  )

  if (!fullDivision) {
    return card
  }

  // The reason the card refuses a click, given once on hover — replacing the
  // two badges that used to say it twice in the card body.
  return <Tooltip title={DIVISION_FULL_MESSAGE}>{card}</Tooltip>
}

export default DivisionCard
